import React, { useCallback, useEffect, useRef, useState } from 'react';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { Box, Fade, IconButton, Modal as MuiModal } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { modalRootCenteredSx } from '../../platform/safeArea';
import { storageGet, storageSet } from '../../platform/storage';

export const POPUP_BANNER_SEEN_KEY = 'popup_banner_seen_id';

function imageUrlOf(banner) {
  if (!banner || typeof banner !== 'object') return '';
  return typeof banner.imageUrl === 'string' ? banner.imageUrl.trim() : '';
}

function preloadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('image load failed'));
    img.src = src;
  });
}

/**
 * Centered in-app campaign overlay for `data.popupBanner` from GET /api/landing.
 * Not a push/FCM banner. Shown only on the home/landing screen after a successful fetch.
 */
export default function LandingPopupBanner({ popupBanner }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const closingRef = useRef(false);

  const dismissible = popupBanner?.dismissible === true;
  const imageUrl = imageUrlOf(popupBanner);

  const persistSeen = useCallback(async (banner) => {
    if (banner?.showOnce === true && banner?.id) {
      await storageSet(POPUP_BANNER_SEEN_KEY, String(banner.id));
    }
  }, []);

  const closePopup = useCallback(({ navigateTo } = {}) => {
    if (closingRef.current) return;
    closingRef.current = true;
    const banner = popupBanner;
    setOpen(false);
    void persistSeen(banner);
    if (navigateTo) navigate(navigateTo);
  }, [navigate, persistSeen, popupBanner]);

  useEffect(() => {
    let cancelled = false;
    closingRef.current = false;
    setOpen(false);

    async function maybeShow() {
      const banner = popupBanner;
      const src = imageUrlOf(banner);
      if (!src) return;

      if (banner.showOnce === true && banner.id) {
        const seenId = await storageGet(POPUP_BANNER_SEEN_KEY);
        if (cancelled) return;
        if (seenId != null && String(seenId) === String(banner.id)) return;
      }

      try {
        await preloadImage(src);
      } catch {
        return;
      }
      if (cancelled) return;

      setOpen(true);
    }

    maybeShow();
    return () => {
      cancelled = true;
    };
  }, [popupBanner]);

  useEffect(() => {
    if (!open) return undefined;
    const seconds = Number(popupBanner?.displayDurationSeconds);
    if (!(seconds > 0)) return undefined;
    const timer = setTimeout(() => {
      closePopup();
    }, seconds * 1000);
    return () => clearTimeout(timer);
  }, [open, popupBanner?.displayDurationSeconds, closePopup]);

  const handleModalClose = (_event, reason) => {
    if (!dismissible && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    closePopup();
  };

  const handleCta = () => {
    const route = typeof popupBanner?.route === 'string' ? popupBanner.route.trim() : '';
    closePopup({ navigateTo: route || undefined });
  };

  if (!imageUrl) return null;

  return (
    <MuiModal
      open={open}
      onClose={handleModalClose}
      closeAfterTransition
      disableEscapeKeyDown={!dismissible}
      BackdropProps={{
        sx: { bgcolor: 'rgba(0, 0, 0, 0.55)' },
        timeout: 200,
      }}
      sx={{
        ...modalRootCenteredSx,
        zIndex: (theme) => theme.zIndex.modal,
      }}
    >
      <Fade in={open} timeout={200}>
        <Box
          sx={{
            position: 'relative',
            width: '86%',
            maxWidth: 360,
            outline: 0,
            transformOrigin: 'center center',
            '@keyframes popupBannerIn': {
              from: { opacity: 0, transform: 'scale(0.95)' },
              to: { opacity: 1, transform: 'scale(1)' },
            },
            animation: open ? 'popupBannerIn 200ms ease-out' : 'none',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              borderRadius: '16px',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt=""
              onClick={handleCta}
              sx={{
                width: '100%',
                height: 'auto',
                aspectRatio: '4 / 5',
                maxHeight: '70vh',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
                cursor: 'pointer',
              }}
            />
          </Box>
          {dismissible ? (
            <IconButton
              aria-label="Close"
              onClick={() => closePopup()}
              sx={{
                position: 'absolute',
                top: -16,
                right: -16,
                width: 32,
                height: 32,
                p: 0,
                bgcolor: '#fff',
                color: 'rgba(0,0,0,0.82)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                '&:hover': { bgcolor: '#fafafa' },
              }}
            >
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          ) : null}
        </Box>
      </Fade>
    </MuiModal>
  );
}
